/**
 * v7.33 — Diálogo pra entregador digitar o PIN de 4 dígitos no momento
 * da entrega. Validação de input acontece no helper `markOrderDelivered`.
 */
import { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { ShieldCheck, X } from 'lucide-react';

export default function DeliveryPinDialog({ open, onClose, onConfirm, orderId, customerName }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Reset on open — padrão clássico de formulário em modal.
    setPin('');
    setError('');
    setSubmitting(false);
    const t = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, [open]);

  const handleChange = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 4);
    setPin(v);
    if (error) setError('');
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!/^\d{4}$/.test(pin)) {
      setError('Digite os 4 dígitos do PIN');
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(pin);
      // Caller fecha o dialog em caso de sucesso
    } catch (err) {
      setError(err?.message || 'PIN inválido');
      setSubmitting(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !submitting) onClose(); }}>
      <DialogContent data-testid="delivery-pin-dialog" className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={20} className="text-emerald-600" />
            Confirmar entrega
          </DialogTitle>
          <DialogDescription>
            Peça ao cliente o <strong>código de 4 dígitos</strong> exibido na tela de rastreio dele
            {orderId ? <> — pedido <strong>#{orderId}</strong></> : null}
            {customerName ? <> ({customerName})</> : null}.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <input
            ref={inputRef}
            data-testid="delivery-pin-input"
            type="text"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            value={pin}
            onChange={handleChange}
            onKeyDown={handleKey}
            disabled={submitting}
            placeholder="0000"
            className="w-full text-center font-mono text-4xl font-black tracking-[0.5em] py-4 border-2 border-slate-200 rounded-xl focus:border-emerald-500 focus:outline-none disabled:opacity-50"
          />
          {error && (
            <p data-testid="delivery-pin-error" className="mt-3 text-sm text-rose-600 font-bold text-center">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <button
            data-testid="delivery-pin-cancel"
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2.5 rounded-lg border-2 border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <X size={14} /> Cancelar
          </button>
          <button
            data-testid="delivery-pin-confirm"
            type="button"
            onClick={handleSubmit}
            disabled={submitting || pin.length !== 4}
            className="px-4 py-2.5 rounded-lg bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <ShieldCheck size={14} /> {submitting ? 'Validando…' : 'Confirmar entrega'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
